/*
 * UV Cart Item Layout v1.3.1 Native Post-Morph Sync
 * Shopify Horizon 4.1.x visual enhancer.
 *
 * This file never mutates cart state and never performs cart network writes.
 * Horizon owns cart updates; this enhancer listens directly to the native
 * shopify:cart:lines-update event and re-applies presentation once the actual
 * cart DOM matches the resolved Shopify cart.
 */

const CONFIG_SELECTOR = '[data-uv-cart-item-layout-config]';
const UV_SYNC_EVENT = 'uv:cart-synced';
const DRAWER_OPEN_EVENT = 'theme-drawer:open';

const SIZE_LABELS = new Map([
  ['XS', 'XS · Extra Small'],
  ['S', 'S · Small'],
  ['M', 'M · Medium'],
  ['L', 'L · Large'],
  ['XL', 'XL'],
  ['XXL', '2XL'],
  ['2XL', '2XL'],
  ['XXXL', '3XL'],
  ['3XL', '3XL'],
  ['XXXXL', '4XL'],
  ['4XL', '4XL'],
  ['XXXXXL', '5XL'],
  ['5XL', '5XL'],
]);

// Migrated from the old EtherLotus cart swatch map, with common Comfort Colors additions.
const COLOR_MAP = new Map(
  Object.entries({
    'chalky mint': '#5CB8B2',
    mustard: '#F2CD00',
    grey: '#716E6A',
    gray: '#716E6A',
    crimson: '#A4123F',
    terracotta: '#E3775E',
    hemp: '#1C4220',
    yam: '#BE531C',
    'island reef': '#8FE2B0',
    graphite: '#1D252D',
    'mystic blue': '#5C88DA',
    blossom: '#F8BED6',
    'light green': '#5C7F71',
    khaki: '#A09074',
    bay: '#94A596',
    violet: '#7474C1',
    watermelon: '#F4364C',
    seafoam: '#487A7B',
    butter: '#F5E1A4',
    berry: '#7E4966',
    denim: '#425563',
    black: '#25282A',
    midnight: '#1B365D',
    moss: '#22372B',
    'true navy': '#041E42',
    navy: '#041E42',
    ivory: '#E7CEB5',
    bone: '#E8DECF',
    sandstone: '#C7B69F',
    grape: '#211551',
    'blue jean': '#647692',
    sage: '#97A798',
    pepper: '#4E4B48',
    espresso: '#553C36',
    'heather purple': '#836995',
    'army green': '#63672F',
    burgundy: '#74243D',
    'heather forest': '#48655B',
    'heather prism ice blue': '#BAD1BE',
    'heather prism lilac': '#C7A1B2',
    'heather midnight navy': '#302E34',
    'heather deep teal': '#516270',
    mauve: '#B17E7B',
    chestnut: '#50321E',
    'pale pink': '#F0C8B4',
    'royal blue': '#024CD4',
    'sports green': '#2A4242',
    'oatmeal heather': '#F2F1E7',
    sandshell: '#F9F1E6',
    'charcoal heather': '#373737',
    'heather grey': '#BABABA',
    'heather gray': '#BABABA',
    'heather peach': '#F3BE8D',
    'charity pink': '#DF6797',
    white: '#F7F6F1',
    natural: '#E8DFCF',
    'blue spruce': '#486B67',
    'flo blue': '#4F86A8',
    crunchberry: '#B85D70',
    banana: '#E8D892',
    orchid: '#B99AB8',
    paprika: '#A84832',
    wine: '#633744',
    'light blue': '#B8CBD5',
    'ice blue': '#BED1D5',
  })
);

function readBoolean(value, fallback = true) {
  if (value == null || value === '') return fallback;
  return value === 'true';
}

function getConfig() {
  const element = document.querySelector(CONFIG_SELECTOR);
  if (!element) return null;

  return {
    enableDrawer: readBoolean(element.dataset.enableDrawer),
    enableCartPage: readBoolean(element.dataset.enableCartPage),
    expandSizeNames: readBoolean(element.dataset.expandSizeNames),
    showColorSwatches: readBoolean(element.dataset.showColorSwatches),
    showComparePrice: readBoolean(element.dataset.showComparePrice),
  };
}

function normalizeOptionValue(value) {
  return String(value || '').replace(/\u00a0/g, ' ').replace(/,\s*$/, '').trim();
}

function normalizeOptionName(value) {
  return String(value || '').replace(/:\s*$/, '').trim();
}

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getRowSurface(row) {
  if (row.closest('#cart-drawer, cart-drawer-component, .cart-drawer__inner')) return 'drawer';
  if (row.closest('.cart-page')) return 'page';
  return null;
}

function getQuantity(row) {
  const input = row.querySelector('.quantity-selector input[name="updates[]"]');
  const quantity = Number.parseInt(input?.value || '1', 10);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function parseMoneyNumber(text) {
  const cleaned = String(text || '').replace(/[^0-9.,-]/g, '');
  if (!cleaned) return null;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  let decimalSeparator = null;

  if (lastDot > lastComma && lastDot !== -1 && cleaned.length - lastDot - 1 <= 2) {
    decimalSeparator = '.';
  } else if (lastComma > lastDot && lastComma !== -1 && cleaned.length - lastComma - 1 <= 2) {
    decimalSeparator = ',';
  }

  let normalized = cleaned;
  if (decimalSeparator === '.') {
    normalized = normalized.replace(/,/g, '');
  } else if (decimalSeparator === ',') {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(/[.,]/g, '');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMoneyLikeStore(amount, fallbackText) {
  const currency = window.Shopify?.currency?.active;
  const locale = document.documentElement.lang || navigator.language || 'en-US';

  if (currency) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (_error) {
      // Fall through to a lightweight formatting fallback.
    }
  }

  const numeric = parseMoneyNumber(fallbackText);
  if (numeric == null || numeric === 0) return fallbackText;
  const ratio = amount / numeric;
  if (!Number.isFinite(ratio)) return fallbackText;

  return fallbackText.replace(/[0-9][0-9.,]*/, amount.toFixed(2));
}

function updateComparePrice(row, config) {
  const priceCell = row.querySelector('.cart-items__price');
  if (!priceCell) return;

  let compare = priceCell.querySelector('.uv-cart-compare-price');

  if (!config.showComparePrice) {
    compare?.remove();
    return;
  }

  const nativeCompare = row.querySelector('.cart-items__unit-price-wrapper s.compare-at-price');
  if (!nativeCompare) {
    compare?.remove();
    return;
  }

  const nativeText = nativeCompare.textContent.trim();
  if (!nativeText) {
    compare?.remove();
    return;
  }

  const quantity = getQuantity(row);
  let displayText = nativeText;

  if (quantity > 1) {
    const unitAmount = parseMoneyNumber(nativeText);
    if (unitAmount != null) displayText = formatMoneyLikeStore(unitAmount * quantity, nativeText);
  }

  if (!compare) {
    compare = document.createElement('s');
    compare.className = 'uv-cart-compare-price';
    compare.setAttribute('aria-label', 'Regular price');
    compare.setAttribute('data-uv-cart-owned', 'compare-price');
    const unitPrice = priceCell.querySelector('.cart-items__price-unit');
    priceCell.insertBefore(compare, unitPrice || null);
  }

  if (compare.textContent !== displayText) compare.textContent = displayText;
}

function getNativeOptionText(element) {
  const clone = element.cloneNode(true);
  clone.querySelectorAll?.('[data-uv-cart-owned], .uv-cart-option__swatch, .uv-cart-option__value').forEach((node) => node.remove());
  return normalizeOptionValue(clone.textContent || '');
}

function enhanceVariant(option, config) {
  const dt = option.querySelector('dt');
  const dd = option.querySelector('dd');
  if (!dt || !dd) return;

  const nativeName = normalizeOptionName(getNativeOptionText(dt));
  const nativeValue = getNativeOptionText(dd);

  // Keep a stable raw value across our own subsequent passes. If Horizon morphs
  // fresh native text into this option, the non-empty native value replaces it.
  if (nativeName) option.dataset.uvCartRawName = nativeName;
  if (nativeValue) option.dataset.uvCartRawValue = nativeValue;

  const name = option.dataset.uvCartRawName || normalizeOptionName(dt.textContent);
  let value = option.dataset.uvCartRawValue || normalizeOptionValue(dd.textContent);
  const handle = slugify(name);

  option.classList.add('uv-cart-option');
  [...option.classList]
    .filter((className) => className.startsWith('uv-cart-option--') && className !== `uv-cart-option--${handle}`)
    .forEach((className) => option.classList.remove(className));
  if (handle) option.classList.add(`uv-cart-option--${handle}`);

  dt.classList.remove('visually-hidden');
  const labelText = `${name}:`;
  if (dt.textContent !== labelText) dt.textContent = labelText;

  if (handle === 'size' && config.expandSizeNames) {
    value = SIZE_LABELS.get(value.toUpperCase()) || value;
  }

  const isColor = handle === 'color' || handle === 'colour';
  let swatch = dd.querySelector('.uv-cart-option__swatch');

  if (isColor && config.showColorSwatches) {
    if (!swatch) {
      swatch = document.createElement('span');
      swatch.className = 'uv-cart-option__swatch';
      swatch.setAttribute('aria-hidden', 'true');
      swatch.setAttribute('data-uv-cart-owned', 'swatch');
      dd.prepend(swatch);
    }

    const color = COLOR_MAP.get(normalizeOptionValue(value).toLowerCase()) || '#dcdcdc';
    swatch.style.setProperty('--uv-cart-swatch-color', color);
  } else {
    swatch?.remove();
  }

  let valueNode = dd.querySelector('.uv-cart-option__value');
  if (!valueNode) {
    // Remove only native text nodes after capturing them. Horizon's nested
    // elements (if any) remain untouched so morphing has less work to reconcile.
    for (const node of [...dd.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    }

    valueNode = document.createElement('span');
    valueNode.className = 'uv-cart-option__value';
    valueNode.setAttribute('data-uv-cart-owned', 'value');
    dd.append(valueNode);
  }

  if (valueNode.textContent !== value) valueNode.textContent = value;
}

function cleanupRow(row) {
  row.classList.remove('uv-cart-item-row', 'uv-cart-item-enhanced');
  row.querySelector('.uv-cart-compare-price')?.remove();

  row.querySelectorAll('.cart-items__variant.uv-cart-option').forEach((option) => {
    option.classList.remove('uv-cart-option');
    [...option.classList]
      .filter((className) => className.startsWith('uv-cart-option--'))
      .forEach((className) => option.classList.remove(className));
  });
}

function enhanceRow(row, config) {
  const surface = getRowSurface(row);
  if (!surface) return;

  const enabled = (surface === 'drawer' && config.enableDrawer) || (surface === 'page' && config.enableCartPage);
  if (!enabled) {
    cleanupRow(row);
    return;
  }

  row.classList.add('uv-cart-item-row');

  row.querySelectorAll('.cart-items__variants .cart-items__variant').forEach((option) => {
    enhanceVariant(option, config);
  });

  updateComparePrice(row, config);
  row.classList.add('uv-cart-item-enhanced');
}

function enhanceAll(config) {
  const allRows = document.querySelectorAll('#cart-drawer .cart-items__table-row, .cart-page .cart-items__table-row');
  allRows.forEach((row) => enhanceRow(row, config));
}

function cartSignatureFromItems(items) {
  return (items || [])
    .map((item) => `${String(item.key || '')}:${Number(item.quantity) || 0}`)
    .sort()
    .join('|');
}

function cartSignatureFromSurface(surface) {
  return [...surface.querySelectorAll('.cart-items__table-row[data-key]')]
    .map((row) => {
      const input = row.querySelector('input[name="updates[]"]');
      const quantity = Number.parseInt(input?.value || input?.getAttribute('value') || '1', 10);
      return `${String(row.dataset.key || '')}:${Number.isFinite(quantity) ? quantity : 1}`;
    })
    .sort()
    .join('|');
}

function getEnabledSurfaces(config) {
  const surfaces = [];
  if (config.enableDrawer) {
    const drawer = document.querySelector('#cart-drawer');
    if (drawer) surfaces.push(drawer);
  }
  if (config.enableCartPage) {
    const page = document.querySelector('.cart-page');
    if (page) surfaces.push(page);
  }
  return surfaces;
}

function waitForCartMorph(config, items, timeout = 260) {
  const expected = cartSignatureFromItems(items);
  if (!getEnabledSurfaces(config).length) return Promise.resolve();

  const isSynced = () => {
    const surfaces = getEnabledSurfaces(config);
    return surfaces.length > 0 && surfaces.every((surface) => cartSignatureFromSurface(surface) === expected);
  };

  if (isSynced()) return Promise.resolve();

  return new Promise((resolve) => {
    let done = false;
    const observers = [];

    const finish = () => {
      if (done) return;
      done = true;
      observers.forEach((observer) => observer.disconnect());
      clearTimeout(timer);
      resolve();
    };

    const check = () => {
      if (isSynced()) finish();
    };

    getEnabledSurfaces(config).forEach((surface) => {
      const observer = new MutationObserver(check);
      observer.observe(surface, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      observers.push(observer);
    });

    const timer = window.setTimeout(finish, timeout);
    queueMicrotask(check);
  });
}

function initialize() {
  if (window.__uvCartItemLayoutRobustInitialized) return;
  window.__uvCartItemLayoutRobustInitialized = true;

  const config = getConfig();
  if (!config) return;

  document.documentElement.classList.toggle('uv-cart-layout-drawer', config.enableDrawer);
  document.documentElement.classList.toggle('uv-cart-layout-page', config.enableCartPage);

  let frame = 0;

  const runEnhance = () => {
    frame = 0;
    enhanceAll(config);
  };

  const scheduleEnhance = () => {
    if (frame) return;
    frame = requestAnimationFrame(runEnhance);
  };

  const handleCartLinesUpdate = (event) => {
    if (!event?.promise || typeof event.promise.then !== 'function') {
      scheduleEnhance();
      return;
    }

    event.promise
      .then(async (result = {}) => {
        if (result?.detail?.didError) return;
        const items = result?.detail?.items;

        // Horizon 4.1.5 morphs its cart from the same standard-event promise.
        // If our callback happens to run first, wait for the actual cart DOM
        // signature instead of guessing with fixed 80/120/250ms delays.
        if (Array.isArray(items)) await waitForCartMorph(config, items);
        scheduleEnhance();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[UV Cart Item Layout] Cart update event promise rejected.', error);
        }
      });
  };

  // Canonical native event name used by Horizon 4.1.5. Direct registration avoids
  // dynamic-import timing and guarantees the enhancer is listening as soon as its
  // module executes.
  document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);

  scheduleEnhance();

  // Structural fallback only. Our own swatches/value spans do not match these
  // selectors, so this cannot recursively retrigger itself.
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches?.('.cart-items__table-row, cart-items-component, .cart-drawer__inner, .cart-page') ||
            node.querySelector?.('.cart-items__table-row, cart-items-component'))
      )
    );

    if (relevant) scheduleEnhance();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener(DRAWER_OPEN_EVENT, scheduleEnhance);
  document.addEventListener(UV_SYNC_EVENT, scheduleEnhance);
  document.addEventListener('shopify:section:load', scheduleEnhance);
  document.addEventListener('shopify:section:select', scheduleEnhance);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
